import { WebClientSize } from "@faire/web-api--source/indigofair/data/WebClientSize";
/**
 * This has to be the lowest common multiple (LCM) of possible component widths
 * in order for every possible width to fit into a whole number of columns.
 * E.g. if we have a
 *  - 5 column layout in the first row
 *  - 12 column layout in second row
 *  - 6 column layout in third row
 * We need to have a grid that can be divided into 5, 12, and 6 columns. 60 is
 * the lowest number of columns that can support that division.
 *
 * Currently, we have a 12 column layout and we support components
 * spanning 1, 2, 3, 4, 5, 6, and 12 columns; the LCM of those is 60.
 */ export var NUM_COLUMNS_GRID_DESKTOP = 60;
/**
 * Mobile does not need support for 5 column layouts.
 * The gaps with 60 columns take up space and overflow the smallest
 * supported screen sizes (e.g. 60 columns x 8px = 480px).
 */ export var NUM_COLUMNS_GRID = 12;
/**
 * Converts a percentage width of a component to the number of columns
 * in the grid layout that the component should span.
 */ export var calcNumColumnsToSpan = function(percentageWidth) {
    var isMobile = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : false, numColumns = arguments.length > 2 ? arguments[2] : void 0;
    return Math.round((numColumns !== null && numColumns !== void 0 ? numColumns : isMobile ? NUM_COLUMNS_GRID : NUM_COLUMNS_GRID_DESKTOP) * ((percentageWidth !== null && percentageWidth !== void 0 ? percentageWidth : 100) / 100));
};
/**
 * Calculates the column span for the item in each screen size based on item visibility.
 * itemWidths only includes as many widths as number of visible items, so we need to account for that.
 * @param itemWidths - The widths of the item in each screen size
 * @param itemVisibility - The screen sizes that the item should be visible on
 * @param currentWidthIndices - The current indices for where in the itemWidth array we are
 * @returns - The column span for the item in each screen size, as well as the updated indices
 * for where in the itemWidth array we are. Returns undefined for the screen size if the item is not visible.
 */ export var getColSpanFromItemWidths = function(itemWidths, itemVisibility, currentWidthIndices, numColumnsPerRow) {
    var _itemWidths_mobile_and_above, _itemWidths_tablet_and_above, _itemWidths_desktop_and_above, _itemWidths_x_large_and_above, _itemWidths_desktop_and_above1;
    var shouldDisplayOnMobile = isItemVisible(itemVisibility, WebClientSize.MOBILE);
    var mobileColSpan = shouldDisplayOnMobile ? calcNumColumnsToSpan((_itemWidths_mobile_and_above = itemWidths.mobile_and_above) === null || _itemWidths_mobile_and_above === void 0 ? void 0 : _itemWidths_mobile_and_above[currentWidthIndices.mobile], true, numColumnsPerRow === null || numColumnsPerRow === void 0 ? void 0 : numColumnsPerRow.mobile_and_above) : undefined;
    var shouldDisplayOnTablet = isItemVisible(itemVisibility, WebClientSize.TABLET);
    var tabletColSpan = shouldDisplayOnTablet ? calcNumColumnsToSpan((_itemWidths_tablet_and_above = itemWidths.tablet_and_above) === null || _itemWidths_tablet_and_above === void 0 ? void 0 : _itemWidths_tablet_and_above[currentWidthIndices.tablet], true, numColumnsPerRow === null || numColumnsPerRow === void 0 ? void 0 : numColumnsPerRow.tablet_and_above) : undefined;
    var shouldDisplayOnDesktop = isItemVisible(itemVisibility, WebClientSize.DESKTOP);
    var desktopColSpan = shouldDisplayOnDesktop ? calcNumColumnsToSpan((_itemWidths_desktop_and_above = itemWidths.desktop_and_above) === null || _itemWidths_desktop_and_above === void 0 ? void 0 : _itemWidths_desktop_and_above[currentWidthIndices.desktop], false, numColumnsPerRow === null || numColumnsPerRow === void 0 ? void 0 : numColumnsPerRow.desktop_and_above) : undefined;
    var shouldDisplayOnXLargeAndAbove = isItemVisible(itemVisibility, WebClientSize.XLARGE_DESKTOP);
    var _itemWidths_x_large_and_above_currentWidthIndices_xl_desktop;
    var xlargeColSpan = shouldDisplayOnXLargeAndAbove ? calcNumColumnsToSpan((_itemWidths_x_large_and_above_currentWidthIndices_xl_desktop = (_itemWidths_x_large_and_above = itemWidths.x_large_and_above) === null || _itemWidths_x_large_and_above === void 0 ? void 0 : _itemWidths_x_large_and_above[currentWidthIndices.xl_desktop]) !== null && _itemWidths_x_large_and_above_currentWidthIndices_xl_desktop !== void 0 ? _itemWidths_x_large_and_above_currentWidthIndices_xl_desktop : (_itemWidths_desktop_and_above1 = itemWidths.desktop_and_above) === null || _itemWidths_desktop_and_above1 === void 0 ? void 0 : _itemWidths_desktop_and_above1[currentWidthIndices.desktop], false, numColumnsPerRow === null || numColumnsPerRow === void 0 ? void 0 : numColumnsPerRow.x_large_and_above) : undefined;
    // itemWidths only includes as many widths as number of visible items.
    // therefore, we need to increment the index only if the item is visible
    // for that particular screen size
    var newWidthIndices = {
        mobile: currentWidthIndices.mobile + (shouldDisplayOnMobile ? 1 : 0),
        tablet: currentWidthIndices.tablet + (shouldDisplayOnTablet ? 1 : 0),
        desktop: currentWidthIndices.desktop + (shouldDisplayOnDesktop ? 1 : 0),
        xl_desktop: currentWidthIndices.xl_desktop + (shouldDisplayOnXLargeAndAbove ? 1 : 0)
    };
    return {
        colSpan: {
            mobile_and_above: mobileColSpan,
            tablet_and_above: tabletColSpan,
            desktop_and_above: desktopColSpan,
            x_large_and_above: xlargeColSpan
        },
        newWidthIndices: newWidthIndices
    };
};
var isItemVisible = function(itemVisibility, clientSize) {
    if (!itemVisibility || itemVisibility.length === 0) {
        return true; // default to visible at all screen sizes
    }
    return itemVisibility.includes(clientSize);
};
export var getItemWidths = function(layoutConfig) {
    return layoutConfig.filter(function(val) {
        var _val_component_index;
        return ((_val_component_index = val.component_index) !== null && _val_component_index !== void 0 ? _val_component_index : -1) >= 0;
    }).sort(function(a, b) {
        var _a_component_index, _b_component_index;
        return ((_a_component_index = a.component_index) !== null && _a_component_index !== void 0 ? _a_component_index : -1) - ((_b_component_index = b.component_index) !== null && _b_component_index !== void 0 ? _b_component_index : -1);
    }).map(function(val) {
        var _val_component_width;
        return (_val_component_width = val.component_width) !== null && _val_component_width !== void 0 ? _val_component_width : 0;
    });
};
export var getItemOrders = function(layoutConfig) {
    return layoutConfig.map(function(config, i) {
        var _config_component_index;
        return {
            componentIndex: (_config_component_index = config.component_index) !== null && _config_component_index !== void 0 ? _config_component_index : -1,
            componentOrder: i
        };
    }).filter(function(val) {
        return val.componentIndex >= 0;
    }).sort(function(a, b) {
        return a.componentIndex - b.componentIndex;
    }).map(function(val) {
        return val.componentOrder;
    });
};
export var getLCMOfNumColumnsGivenWidths = function(widths) {
    if (widths.length === 0) {
        return NUM_COLUMNS_GRID_DESKTOP;
    }
    var numColumnsPerRow = widths.map(function(width) {
        return Math.floor(100 / width);
    });
    var gcd = function(a, b) {
        return !b ? a : gcd(b, a % b);
    };
    return numColumnsPerRow.reduce(function(a, b) {
        return a * b / gcd(a, b);
    });
};
